import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { MaskedLine, FadeUp } from "@/components/MaskedReveal";

const EASE = [0.76, 0, 0.24, 1];

export default function ServiceDetail({ config }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <>
      <Nav />
      <main className="bg-[#050505] text-white">
        {/* HERO */}
        <section
          ref={ref}
          className="relative h-[88svh] w-full overflow-hidden"
          data-testid={`service-hero-${config.slug}`}
        >
          <motion.div style={{ y }} className="absolute inset-0 w-full h-[130%] -top-[15%]">
            <img
              src={config.heroImage}
              alt={config.line1 + " " + config.line2}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 hero-vignette" />
          </motion.div>

          <motion.div
            style={{ y: heroTextY }}
            className="relative z-10 h-full flex flex-col justify-between mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 pt-32 pb-14"
          >
            <div className="flex items-start justify-between">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 1, ease: EASE }}
                className="font-mono-label text-white/60"
              >
                <span className="text-[#FF5A00]">◉</span> {config.category}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 1, ease: EASE }}
                className="hidden md:block font-mono-label text-white/60 text-right"
              >
                Program Code · {config.code}
              </motion.div>
            </div>

            <div className="flex-1 flex flex-col justify-end">
              <h1 className="font-display uppercase leading-[0.86] tracking-[-0.04em] text-[12vw] md:text-[8.5vw]">
                <MaskedLine delay={0.4} animate={{ y: "0%" }}>
                  {config.line1}
                </MaskedLine>
                <MaskedLine
                  delay={0.6}
                  animate={{ y: "0%" }}
                  className={config.line2Italic ? "pl-[6vw] md:pl-[10vw]" : ""}
                >
                  {config.line2Italic && (
                    <span className="font-serif-italic italic pr-4 text-[0.85em]">
                      {config.line2Italic}
                    </span>
                  )}
                  {config.line2}
                </MaskedLine>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 1, ease: EASE }}
                className="mt-8 md:mt-12 max-w-2xl font-serif-italic text-white/85 text-xl md:text-2xl leading-relaxed"
              >
                {config.tagline}
              </motion.p>
            </div>

            <div className="flex items-end justify-between font-mono-label text-white/50 text-[10px]">
              <Link
                to="/"
                className="hover:text-[#FF5A00] transition-colors duration-500"
                data-testid={`service-back-${config.slug}`}
              >
                ← El Elyon Air · Home
              </Link>
              <div className="hidden md:block">Scroll — Detail</div>
              <div>{config.code}</div>
            </div>
          </motion.div>
        </section>

        {/* LEAD + BODY */}
        <section className="py-24 md:py-40 border-t border-white/10">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <FadeUp>
                <div className="font-mono-label text-white/50 mb-6">
                  <span className="text-[#FF5A00]">◉</span> Overview · 001
                </div>
              </FadeUp>
              <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-tight">
                <MaskedLine>The service</MaskedLine>
                <MaskedLine delay={0.12}>
                  <span className="font-serif-italic italic text-white/70">
                    in detail.
                  </span>
                </MaskedLine>
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 space-y-6">
              <FadeUp>
                <p className="text-white/85 text-lg md:text-xl leading-relaxed">
                  {config.lead}
                </p>
              </FadeUp>
              {config.paragraphs.map((p, i) => (
                <FadeUp key={i} delay={0.08 + i * 0.08}>
                  <p className="text-white/65 text-base md:text-lg leading-relaxed">
                    {p}
                  </p>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* HIGHLIGHT STRIP */}
        <section className="py-16 md:py-24 border-y border-white/10 bg-[#0A0A0C]">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            {config.highlights.map((h, i) => (
              <motion.div
                key={h.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
                transition={{ duration: 0.9, ease: EASE, delay: i * 0.08 }}
                className="border-l border-white/15 pl-5"
                data-testid={`highlight-${h.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="font-mono-label text-white/40 mb-2">{h.label}</div>
                <div className="font-display text-xl md:text-2xl uppercase leading-tight">
                  {h.value}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CLOSING */}
        <section className="py-24 md:py-32">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <FadeUp>
                <div className="font-mono-label text-white/50 mb-6">
                  <span className="text-[#FF5A00]">◉</span> Closing
                </div>
              </FadeUp>
              <h3 className="font-serif-italic text-[#FF5A00] text-3xl md:text-5xl leading-[1.1] max-w-4xl">
                “{config.closing}”
              </h3>
            </div>
            <div className="md:col-span-4 md:col-start-9 flex flex-col gap-4">
              <Link
                to="/#contact"
                className="btn-outline btn-solid"
                data-testid={`service-cta-${config.slug}`}
              >
                Request This Service
                <span aria-hidden>→</span>
              </Link>
              <Link
                to="/"
                className="btn-outline"
                data-testid={`service-cta-home-${config.slug}`}
              >
                <span aria-hidden>←</span>
                El Elyon Air · Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
